// What mode B's two RPCs said they did, decoded and never assumed.
//
// Run: deno test --allow-read src/features/pnlAnalytics/clearResultDecode.test.ts
// Run: npm run verify:pnl-clear-result-decode
//
// WHY THIS IS A MODULE AND NOT THREE LINES INSIDE THE HOOK. Both RPCs
// (supabase/migrations/20260915000300_pnl_value_archive.sql) return a jsonb
// object of counts, and those counts are the ONLY evidence the client has that
// a destructive act did what it stated. "No error" is not evidence: a Supabase
// call refused by RLS resolves with neither rows nor an error. So the decoding
// is a rule, the rule is tested, and it lives where a test can reach it: no
// React and no supabase import here, because Deno resolves a type by loading
// its whole file and neither of those loads under Deno.
//
// THE RULE ITSELF. A missing count is never a zero. Reporting "0 values
// cleared" for a response that did not answer the question is worse than
// reporting failure, because it tells someone the money is still there. Every
// field is required, and a response that does not carry it throws with the
// field named.
//
// The skipped count is part of the same rule. Restore inserts with ON CONFLICT
// DO NOTHING against the value table's unique key, so a month somebody retyped
// after the clear is deliberately never overwritten by the older archived
// number. Those rows come back as skipped and the number is stated. A silent
// partial restore is the same class of defect as a silent partial clear.

/** What pnl_clear_project_category actually removed. */
export interface ClearResult {
  batchId: string;
  projectId: string;
  category: string;
  /** Every row deleted, both sources. */
  deletedCount: number;
  /** Manual rows written to the archive. deletedCount minus the Xero rows. */
  archivedCount: number;
  /** The sum of the archived manual rows. Xero money is not archived, by design. */
  archivedTotal: number;
  /** Deleted and not archived, because the next Xero sync reproduces them. */
  deletedXeroCount: number;
}

/** What pnl_restore_value_archive put back, and what it deliberately did not. */
export interface RestoreResult {
  batchId: string;
  projectId: string;
  category: string;
  restoredCount: number;
  /** Months where a newer number had been typed since the clear. Never overwritten. */
  skippedCount: number;
}

/** The shape of a PostgREST error, narrowed to the two fields this reads. */
export interface RpcRefusal {
  message: string;
  code?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requireString(source: Record<string, unknown>, key: string, fn: string): string {
  const value = source[key];
  if (typeof value !== "string" || value === "") {
    throw new Error(`${fn} returned no ${key}, so what it did cannot be confirmed`);
  }
  return value;
}

/**
 * A count, accepting the numeric string PostgREST can produce for a numeric
 * inside jsonb. A boolean is rejected on purpose: Number(true) is 1, which
 * would turn a shape error into a plausible looking count of one.
 */
function requireNumber(source: Record<string, unknown>, key: string, fn: string): number {
  const value = source[key];
  const parsed = typeof value === "number"
    ? value
    : typeof value === "string" && value.trim() !== ""
    ? Number(value)
    : NaN;
  if (!Number.isFinite(parsed)) {
    throw new Error(`${fn} returned no ${key}, so what it did cannot be confirmed`);
  }
  return parsed;
}

export function decodeClearResult(data: unknown): ClearResult {
  const fn = "pnl_clear_project_category";
  if (!isRecord(data)) throw new Error(`${fn} returned no result object, so nothing can be confirmed`);
  return {
    batchId: requireString(data, "batch_id", fn),
    projectId: requireString(data, "project_id", fn),
    category: requireString(data, "category", fn),
    deletedCount: requireNumber(data, "deleted_count", fn),
    archivedCount: requireNumber(data, "archived_count", fn),
    archivedTotal: requireNumber(data, "archived_total", fn),
    deletedXeroCount: requireNumber(data, "deleted_xero_count", fn),
  };
}

export function decodeRestoreResult(data: unknown): RestoreResult {
  const fn = "pnl_restore_value_archive";
  if (!isRecord(data)) throw new Error(`${fn} returned no result object, so nothing can be confirmed`);
  return {
    batchId: requireString(data, "batch_id", fn),
    projectId: requireString(data, "project_id", fn),
    category: requireString(data, "category", fn),
    restoredCount: requireNumber(data, "restored_count", fn),
    skippedCount: requireNumber(data, "skipped_count", fn),
  };
}

/**
 * The SQLSTATE the RPCs raise, in the words the person in front of the screen
 * needs, including what to do next.
 *
 * 40001 carries its own flag rather than only a message, because it is the one
 * refusal meaning "your figures are out of date", not "you did something
 * wrong", and the dialog answers it by reloading rather than by apologising.
 */
export function describeClearRefusal(error: RpcRefusal): { message: string; stale: boolean } {
  if (error.code === "40001") {
    return {
      message:
        "The values under this category changed while the figures were on screen, so nothing was cleared. " +
        "They have been reloaded, so check them and try again.",
      stale: true,
    };
  }
  if (error.code === "42501") {
    return {
      message: "Nothing was cleared. This needs an organisation admin role and financial access, together.",
      stale: false,
    };
  }
  if (error.code === "42883") {
    return {
      message:
        "Nothing was cleared. This database has no clear function, so the migration that adds it has not been applied.",
      stale: false,
    };
  }
  return { message: `Nothing was cleared. ${error.message}`, stale: false };
}

/** The same, for the restore side, which has no stale case of its own. */
export function describeRestoreRefusal(error: RpcRefusal): string {
  if (error.code === "42501") {
    return "Nothing was restored. This needs an organisation admin role and financial access, together.";
  }
  if (error.code === "22023") {
    return "Nothing was restored. This batch has already been restored, or it holds no cleared rows.";
  }
  if (error.code === "42883") {
    return "Nothing was restored. This database has no restore function, so the migration that adds it has not been applied.";
  }
  return `Nothing was restored. ${error.message}`;
}

/** One line stating what a clear removed, split the way the archive splits it. */
export function describeClearOutcome(result: ClearResult): string {
  const archived = `${result.archivedCount} typed ${result.archivedCount === 1 ? "value" : "values"} moved to the archive`;
  if (result.deletedXeroCount === 0) return `${archived}.`;
  return (
    `${archived}, and ${result.deletedXeroCount} Xero ${result.deletedXeroCount === 1 ? "value" : "values"} removed. ` +
    "The Xero ones return on the next sync unless the account mapping changes."
  );
}

/** One line stating what a restore did and what it deliberately left alone. */
export function describeRestoreOutcome(result: RestoreResult): string {
  const restored = `${result.restoredCount} ${result.restoredCount === 1 ? "value" : "values"} returned to ${result.category}`;
  if (result.skippedCount === 0) return `${restored}.`;
  return (
    `${restored}. ${result.skippedCount} ${result.skippedCount === 1 ? "month was" : "months were"} left alone, ` +
    "because a newer number had been typed there since the clear."
  );
}
